import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  FileText, Upload, Download, Eye, Trash2, CheckCircle, XCircle,
  Clock, AlertTriangle, Calendar, Search, Filter, Building2,
  FileCheck, FileWarning, FileClock, Archive, RefreshCw, Loader2,
  ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  'Valido': { color: 'bg-green-500', icon: FileCheck, label: 'Válido' },
  'Pendente_Validacao': { color: 'bg-amber-500', icon: FileClock, label: 'Pendente' },
  'Expirado': { color: 'bg-red-500', icon: FileWarning, label: 'Expirado' },
  'Rejeitado': { color: 'bg-red-600', icon: XCircle, label: 'Rejeitado' },
  'Arquivado': { color: 'bg-slate-400', icon: Archive, label: 'Arquivado' }
};

export default function DocumentosPage() {
  const [loading, setLoading] = useState(true);
  const [documentos, setDocumentos] = useState([]);
  const [alertas, setAlertas] = useState(null);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [instituicoes, setInstituicoes] = useState([]);
  
  // Filters
  const [filtroInstituicao, setFiltroInstituicao] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  // Upload modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    instituicao_id: '',
    tipo_documento: '',
    nome: '',
    descricao: '',
    data_validade: '',
    tags: '',
    requer_renovacao: false,
    dias_alerta_renovacao: 30
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // Detail/Validation modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [validationComment, setValidationComment] = useState('');

  useEffect(() => {
    loadData();
  }, [filtroInstituicao, filtroTipo, filtroStatus]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Build query params
      const params = new URLSearchParams();
      if (filtroInstituicao) params.append('instituicao_id', filtroInstituicao);
      if (filtroTipo) params.append('tipo', filtroTipo);
      if (filtroStatus) params.append('status', filtroStatus);
      
      const [docsRes, alertasRes, tiposRes, instRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documentos?${params}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documentos/alertas`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documentos/tipos`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/instituicoes`, { headers })
      ]);
      
      const [docsData, alertasData, tiposData, instData] = await Promise.all([
        docsRes.json(),
        alertasRes.json(),
        tiposRes.json(),
        instRes.json()
      ]);
      
      setDocumentos(docsData);
      setAlertas(alertasData);
      setTiposDocumento(tiposData);
      setInstituicoes(instData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Ficheiro demasiado grande (máx. 10MB)');
        return;
      }
      setSelectedFile(file);
      if (!uploadForm.nome) {
        setUploadForm(prev => ({ ...prev, nome: file.name.replace(/\.[^/.]+$/, "") }));
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.instituicao_id || !uploadForm.tipo_documento || !uploadForm.nome) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        // Send as JSON body instead of query params
        const payload = {
          instituicao_id: uploadForm.instituicao_id,
          tipo_documento: uploadForm.tipo_documento,
          nome: uploadForm.nome,
          file_content: base64,
          filename: selectedFile.name,
          descricao: uploadForm.descricao || null,
          data_validade: uploadForm.data_validade || null,
          tags: uploadForm.tags || null,
          requer_renovacao: uploadForm.requer_renovacao,
          dias_alerta_renovacao: uploadForm.dias_alerta_renovacao
        };
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documentos/upload`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        
        if (response.ok) {
          toast.success('Documento carregado com sucesso!');
          setUploadModalOpen(false);
          resetUploadForm();
          loadData();
        } else {
          const err = await response.json();
          toast.error(err.detail || 'Erro ao carregar documento');
        }
        setUploading(false);
      };
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      toast.error('Erro ao carregar documento');
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadForm({
      instituicao_id: '',
      tipo_documento: '',
      nome: '',
      descricao: '',
      data_validade: '',
      tags: '',
      requer_renovacao: false,
      dias_alerta_renovacao: 30
    });
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openDetail = async (doc) => {
    setSelectedDoc(doc);
    setDetailModalOpen(true);
    setValidationComment('');
  };

  const handleValidation = async (aprovado) => {
    if (!selectedDoc) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documentos/${selectedDoc.id}/validar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          aprovado,
          comentario: validationComment
        })
      });
      
      if (response.ok) {
        toast.success(aprovado ? 'Documento aprovado!' : 'Documento rejeitado');
        setDetailModalOpen(false);
        loadData();
      } else {
        toast.error('Erro na validação');
      }
    } catch (error) {
      toast.error('Erro na validação');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documentos/${doc.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Create download link from base64
        const byteCharacters = atob(data.content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.mime_type || 'application/octet-stream' });
        
        // Create object URL and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = url;
        link.download = data.filename || doc.nome || 'documento';
        
        // Append to body, click, and cleanup
        document.body.appendChild(link);
        link.click();
        
        // Cleanup after a short delay to ensure download starts
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
        
        toast.success('Download iniciado');
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Erro no download');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erro no download');
    }
  };

  const handleArchive = async (doc) => {
    if (!confirm('Tem certeza que deseja arquivar este documento?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documentos/${doc.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Documento arquivado');
        loadData();
      } else {
        toast.error('Erro ao arquivar');
      }
    } catch (error) {
      toast.error('Erro ao arquivar');
    }
  };

  const filteredDocs = documentos.filter(doc => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return doc.nome?.toLowerCase().includes(search) ||
             doc.instituicao_nome?.toLowerCase().includes(search) ||
             doc.tipo_documento?.toLowerCase().includes(search);
    }
    return true;
  });

  // Sort filtered docs
  const sortedDocs = [...filteredDocs].sort((a, b) => {
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    
    // Handle dates
    if (sortConfig.key === 'data_validade' || sortConfig.key === 'created_at') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }
    
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
    if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;
    
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    const aStr = String(aVal).toLowerCase();
    const bStr = String(bVal).toLowerCase();
    
    if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ChevronsUpDown className="w-4 h-4 text-slate-400 ml-1" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-[#0066CC] ml-1" />
      : <ChevronDown className="w-4 h-4 text-[#0066CC] ml-1" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="documentos-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documentos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <FileText className="w-8 h-8 text-[#0066CC]" />
            Gestão de Documentos
          </h1>
          <p className="text-slate-500 mt-1">Gestão documental com conformidade RGPD</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={() => setUploadModalOpen(true)} data-testid="upload-btn">
            <Upload className="w-4 h-4 mr-2" />
            Carregar Documento
          </Button>
        </div>
      </div>

      {/* Alertas */}
      {alertas && (alertas.expirados?.length > 0 || alertas.a_expirar_7_dias?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {alertas.expirados?.length > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-red-600">{alertas.expirados.length}</p>
                    <p className="text-sm text-red-600">Documentos Expirados</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {alertas.a_expirar_7_dias?.length > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-amber-600">{alertas.a_expirar_7_dias.length}</p>
                    <p className="text-sm text-amber-600">A expirar em 7 dias</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {alertas.total_pendentes_validacao > 0 && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                    <FileClock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-blue-600">{alertas.total_pendentes_validacao}</p>
                    <p className="text-sm text-blue-600">Pendentes Validação</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar documentos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={filtroInstituicao || "all"} onValueChange={(v) => setFiltroInstituicao(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-instituicao">
                <SelectValue placeholder="Instituição" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {instituicoes.map(inst => (
                  <SelectItem key={inst.id} value={inst.id}>{inst.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroTipo || "all"} onValueChange={(v) => setFiltroTipo(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-tipo">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {tiposDocumento.map(tipo => (
                  <SelectItem key={tipo.codigo} value={tipo.codigo}>{tipo.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroStatus || "all"} onValueChange={(v) => setFiltroStatus(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="Valido">Válido</SelectItem>
                <SelectItem value="Pendente_Validacao">Pendente</SelectItem>
                <SelectItem value="Expirado">Expirado</SelectItem>
                <SelectItem value="Arquivado">Arquivado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Documentos ({filteredDocs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sortedDocs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Nenhum documento encontrado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-slate-100"
                    onClick={() => handleSort('nome')}
                  >
                    <div className="flex items-center">
                      Documento
                      {getSortIcon('nome')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-slate-100"
                    onClick={() => handleSort('instituicao_nome')}
                  >
                    <div className="flex items-center">
                      Instituição
                      {getSortIcon('instituicao_nome')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-slate-100"
                    onClick={() => handleSort('tipo_documento')}
                  >
                    <div className="flex items-center">
                      Tipo
                      {getSortIcon('tipo_documento')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-slate-100"
                    onClick={() => handleSort('data_validade')}
                  >
                    <div className="flex items-center">
                      Validade
                      {getSortIcon('data_validade')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-slate-100"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      {getSortIcon('status')}
                    </div>
                  </TableHead>
                  <TableHead>Tamanho</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedDocs.map((doc) => {
                  const statusConfig = STATUS_CONFIG[doc.status] || STATUS_CONFIG['Valido'];
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <TableRow key={doc.id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="font-medium">{doc.nome}</p>
                            <p className="text-xs text-slate-400">{doc.original_filename || doc.filename}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{doc.instituicao_nome || '-'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{doc.tipo_documento}</Badge>
                      </TableCell>
                      <TableCell>
                        {doc.data_validade ? (
                          <div className="text-sm">
                            <p>{new Date(doc.data_validade).toLocaleDateString('pt-PT')}</p>
                            {doc.dias_para_expirar !== null && (
                              <p className={`text-xs ${doc.dias_para_expirar < 0 ? 'text-red-500' : doc.dias_para_expirar <= 30 ? 'text-amber-500' : 'text-slate-400'}`}>
                                {doc.dias_para_expirar < 0 
                                  ? `Expirado há ${Math.abs(doc.dias_para_expirar)} dias`
                                  : `${doc.dias_para_expirar} dias restantes`}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig.color} text-white`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {formatFileSize(doc.file_size)}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDetail(doc)}
                            data-testid={`view-${doc.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownload(doc)}
                            data-testid={`download-${doc.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => handleArchive(doc)}
                            data-testid={`archive-${doc.id}`}
                          >
                            <Archive className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Carregar Documento</DialogTitle>
            <DialogDescription>
              Adicione um novo documento ao sistema
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Instituição *</Label>
              <Select 
                value={uploadForm.instituicao_id} 
                onValueChange={(v) => setUploadForm(prev => ({ ...prev, instituicao_id: v }))}
              >
                <SelectTrigger data-testid="upload-instituicao">
                  <SelectValue placeholder="Selecione a instituição" />
                </SelectTrigger>
                <SelectContent>
                  {instituicoes.map(inst => (
                    <SelectItem key={inst.id} value={inst.id}>{inst.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Tipo de Documento *</Label>
              <Select 
                value={uploadForm.tipo_documento} 
                onValueChange={(v) => setUploadForm(prev => ({ ...prev, tipo_documento: v }))}
              >
                <SelectTrigger data-testid="upload-tipo">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {tiposDocumento.map(tipo => (
                    <SelectItem key={tipo.codigo} value={tipo.codigo}>{tipo.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Ficheiro *</Label>
              <div className="mt-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  data-testid="file-input"
                />
                {selectedFile && (
                  <p className="text-sm text-slate-500 mt-1">
                    {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </p>
                )}
              </div>
            </div>
            
            <div>
              <Label>Nome do Documento *</Label>
              <Input
                value={uploadForm.nome}
                onChange={(e) => setUploadForm(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Ex: Estatutos 2024"
                data-testid="upload-nome"
              />
            </div>
            
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={uploadForm.descricao}
                onChange={(e) => setUploadForm(prev => ({ ...prev, descricao: e.target.value }))}
                placeholder="Descrição opcional..."
                rows={2}
              />
            </div>
            
            <div>
              <Label>Data de Validade</Label>
              <Input
                type="date"
                value={uploadForm.data_validade}
                onChange={(e) => setUploadForm(prev => ({ ...prev, data_validade: e.target.value }))}
                data-testid="upload-validade"
              />
            </div>
            
            <div>
              <Label>Tags (separadas por vírgula)</Label>
              <Input
                value={uploadForm.tags}
                onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="Ex: legal, obrigatório, anual"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Alertar para renovação</Label>
              <Switch
                checked={uploadForm.requer_renovacao}
                onCheckedChange={(v) => setUploadForm(prev => ({ ...prev, requer_renovacao: v }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadModalOpen(false); resetUploadForm(); }}>
              Cancelar
            </Button>
            <Button onClick={handleUpload} disabled={uploading} data-testid="upload-submit">
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  A carregar...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Carregar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail/Validation Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes do Documento</DialogTitle>
          </DialogHeader>
          
          {selectedDoc && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Nome</Label>
                  <p className="font-medium">{selectedDoc.nome}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Tipo</Label>
                  <p>{selectedDoc.tipo_documento}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Instituição</Label>
                  <p>{selectedDoc.instituicao_nome}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Status</Label>
                  <Badge className={`${STATUS_CONFIG[selectedDoc.status]?.color || 'bg-slate-500'} text-white`}>
                    {STATUS_CONFIG[selectedDoc.status]?.label || selectedDoc.status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-slate-500">Data Upload</Label>
                  <p>{new Date(selectedDoc.data_upload).toLocaleDateString('pt-PT')}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Validade</Label>
                  <p>{selectedDoc.data_validade ? new Date(selectedDoc.data_validade).toLocaleDateString('pt-PT') : '-'}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Tamanho</Label>
                  <p>{formatFileSize(selectedDoc.file_size)}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Versão</Label>
                  <p>v{selectedDoc.versao}</p>
                </div>
              </div>
              
              {selectedDoc.descricao && (
                <div>
                  <Label className="text-slate-500">Descrição</Label>
                  <p className="text-sm">{selectedDoc.descricao}</p>
                </div>
              )}
              
              {selectedDoc.status === 'Pendente_Validacao' && (
                <div className="border-t pt-4 space-y-4">
                  <Label>Validação</Label>
                  <Textarea
                    placeholder="Comentário (opcional)"
                    value={validationComment}
                    onChange={(e) => setValidationComment(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleValidation(true)}
                      data-testid="approve-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Aprovar
                    </Button>
                    <Button 
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      onClick={() => handleValidation(false)}
                      data-testid="reject-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Rejeitar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>
              Fechar
            </Button>
            {selectedDoc && (
              <Button onClick={() => handleDownload(selectedDoc)}>
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
